//@import dependencies
import React, {useState, useEffect} from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { Form } from 'react-bootstrap'
import moment from 'moment';
//@end

//@import components
import ReactSelectComponent from 'app_components/commons/reactSelect'
import IconComponent from 'app_components/commons/icons'
//@end

// @import hooks
import {useColors} from 'app_hooks/useColors'
import { ScreenNotificationsUtil } from 'app_utils/screenNotification'
import { I18nUtil } from 'app_utils/i18n'
// @end

// @INFO Import types
// @end

// @INFO Import services
import LandingService from 'app_services/admin/landings/landingService'
import ReactSelectService from 'app_services/components/reactSelectService'
// @end

// @INFO Import Styles
import './modalAllianceForm.scss'
import { IAllianceFields, ILandingAlliance } from 'app_types/admin/landings/landingTypes'

const uuid = require('react-uuid')

// @ADD Component types
interface IModalAllianceFormModuleLayoutProps {
  source: string
  open          : boolean
  customErrors? : any
  editData?     : ILandingAlliance
  handleClose   : (data:any) => void
  fields?: IAllianceFields[]
}

const ModalAllianceFormModuleLayout: React.FC<IModalAllianceFormModuleLayoutProps> = (props) => {

  const i18nUtil = new I18nUtil()
  const screenNotifications = new ScreenNotificationsUtil()
  const landingService = new LandingService()
  const reactSelectService = new ReactSelectService()

  const [registerStatus, setRegisterStatus] = useState<boolean>(false)
  const [processing, updateProcessing] = useState<boolean>(false)

  const [colors] = useColors()

  const {
    open,
    source,
    editData,
    handleClose,
    fields
  } = props

  const formHook = useForm()
  const { t } = useTranslation()

  useEffect(() => {
    if (editData) {
      setRegisterStatus((editData?.status) ? editData?.status : false)
    }
  }, [editData])

  const getProgramOptions = () => {
    if (editData?.programs) {
      const options: {value: string, label: string}[] = editData?.programs.reduce((accum: any, element: string) => {
        accum.push({value: element, label: element})
        return accum;
      }, [])
      return options
    }
    return []
  }

  const handleCloseModal = (data: any) => {
    handleClose(data)
  }

  const handleSubmit = async (data: any) => {
    updateProcessing(true)
    if (data?.typeAgreement) data.typeAgreement = reactSelectService.formatReactSelectResponse(data.typeAgreement)
    if (data?.programs) data.programs = reactSelectService.formatReactSelectResponse(data.programs)

    const landingResponse = await landingService.saveAlliance(
      {...data, slug: source},
    )

    if (landingResponse) {
      screenNotifications.fireToast(
        {
          message: `${i18nUtil.getTranslate('components.screen_notifications.update')}`,
        },
        {
          position: 'top',
        }
      )
      updateProcessing(false)
      handleClose({status: true, data})
    }
    updateProcessing(false)
  }

  const handleChangeStatus = (e: any) => {
    setRegisterStatus(!registerStatus)
  }

  return(
    <div className=''>
      <Modal
        show={open}
        onHide={() => handleCloseModal({ status: false })}
        className="modal_alliance_form-module"
        size='lg'
      >
        <Modal.Header>
          <Modal.Title>
            {t('modules.modal_alliance_form.title')}
          </Modal.Title>
          <div className="close-button" onClick={() => handleCloseModal({status: false})}>
            <IconComponent
              color={colors.secondary}
              width='1.5rem'
              height='1.5rem'
              fontawesome={{
                icon: 'fas fa-times-circle',
              }}
            />
          </div>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={formHook.handleSubmit(handleSubmit)}>
            <input
              type="hidden"
              name="unique"
              defaultValue={(editData?.unique) ?editData?.unique : uuid()}
              ref={formHook.register({ required: true })}
            />
            <div className="col-md-12">
              <div className="form-group">
                <Form.Check
                  type='switch'
                  id='status'
                  name='status'
                  label={t('labels.modal_alliance_form.status')}
                  onChange={(event: any) => handleChangeStatus(event)}
                  checked={registerStatus}
                  ref={formHook.register()}
                />
              </div>
            </div>
            <div className="col-12">
              <div className="form-group">
                <label htmlFor='typeAgreement'>{t('labels.modal_alliance_form.typeAgreement')}</label>
                {formHook.errors?.typeAgreement && <div className='invalid-feedback'>{t('errors.this_field_is_required')}</div>}
                <ReactSelectComponent
                  identifier='typeAgreement'
                  options={landingService.getTypeAgreementList}
                  formHooks={formHook}
                  defaultValue={(editData?.typeAgreement) ? landingService.getTypeAgreementList.filter((i) => i.value === editData?.typeAgreement) : []}
                  isMulti={false}
                  required={true}
                />
              </div>
            </div>
            <div className="col-12">
              <div className="form-group">
                <label htmlFor='agreementDescription'>{t('labels.modal_alliance_form.agreementDescription')}</label>
                {formHook.errors?.agreementDescription && <div className='invalid-feedback'>{t('errors.this_field_is_required')}</div>}
                <input
                  id='agreementDescription'
                  name='agreementDescription'
                  className={`form-control ${formHook.errors?.agreementDescription && 'is-invalid'}`}
                  ref={formHook.register({ required: false })}
                  defaultValue={editData?.agreementDescription}
                />
              </div>
            </div>
            <div className="col-12">
              <div className='form-group'>
                <label htmlFor='logo'>{t('labels.modal_alliance_form.logo')}</label>
                {formHook.errors?.logo && <div className='invalid-feedback'>{t('errors.this_field_is_required')}</div>}
                <input
                  id='logo'
                  name='logo'
                  className='form-control-file'
                  type='file'
                  accept='image/png, image/jpeg'
                  ref={formHook.register({ required: false })}
                />
                {formHook.errors?.logo && <div className='custom-invalid-feedback'>{t('errors.this_field_is_required')}</div>}
              </div>
            </div>
            {editData?.logoUrl && (
              <>
                <input type="hidden" name="logoUrl" value={editData?.logoUrl} ref={formHook.register({ required: true })} />
                <div className="col-md-12">
                  <div className="form-group">
                      <div className="cover-container">
                        <img src={editData?.logoFile} alt=""/>
                      </div>
                  </div>
                </div>
              </>
            )}
            <div className="col-12">
              <div className="form-group">
                <label htmlFor='name'>{t('labels.modal_alliance_form.name')}</label>
                {formHook.errors?.name && <div className='invalid-feedback'>{t('errors.this_field_is_required')}</div>}
                <input
                  id='name'
                  name='name'
                  className={`form-control ${formHook.errors?.name && 'is-invalid'}`}
                  type='text'
                  ref={formHook.register({ required: true })}
                  defaultValue={editData?.name}
                />
              </div>
            </div>
            {fields && fields.includes('country') ? (
              <div className="col-12">
                <div className="form-group">
                  <label htmlFor='country'>{t('labels.modal_alliance_form.country')}</label>
                  {formHook.errors?.country && <div className='invalid-feedback'>{t('errors.this_field_is_required')}</div>}
                  <input
                    id='country'
                    name='country'
                    className={`form-control ${formHook.errors?.country && 'is-invalid'}`}
                    type='text'
                    ref={formHook.register({ required: false })}
                    defaultValue={editData?.country}
                  />
                </div>
              </div>
            ): null}
            {fields && fields.includes('regional') ? (
              <div className="col-12">
                <div className="form-group">
                  <label htmlFor='regional'>{t('labels.modal_alliance_form.regional')}</label>
                  {formHook.errors?.regional && <div className='invalid-feedback'>{t('errors.this_field_is_required')}</div>}
                  <input
                    id='regional'
                    name='regional'
                    className={`form-control ${formHook.errors?.regional && 'is-invalid'}`}
                    type='text'
                    ref={formHook.register({ required: false })}
                    defaultValue={editData?.regional}
                  />
                </div>
              </div>
            ): null}
            {fields && fields.includes('sectional') ? (
              <div className="col-12">
                <div className="form-group">
                  <label htmlFor='sectional'>{t('labels.modal_alliance_form.sectional')}</label>
                  {formHook.errors?.sectional && <div className='invalid-feedback'>{t('errors.this_field_is_required')}</div>}
                  <input
                    id='sectional'
                    name='sectional'
                    className={`form-control ${formHook.errors?.sectional && 'is-invalid'}`}
                    type='text'
                    ref={formHook.register({ required: false })}
                    defaultValue={editData?.sectional}
                  />
                </div>
              </div>
            ): null}
            {fields && fields.includes('creationYear') ? (
              <div className="col-md-12">
                <div className="form-group">
                  <label htmlFor='creationYear'>{t('labels.modal_alliance_form.creationYear')}</label>
                  <input
                    type='date'
                    className='form-control'
                    name='creationYear'
                    defaultValue={editData?.creationYear ? moment.utc(editData?.creationYear).format('YYYY-MM-DD') : undefined}
                    ref={formHook.register({ required: false })}
                  />
                </div>
              </div>
            ): null}
            {fields && fields.includes('programs') ? (
              <div className="col-12 col-md-12">
                <div className="form-group">
                  <label htmlFor='programs'>{t('labels.modal_alliance_form.programs')}</label>
                  {formHook.errors?.programs && <div className='invalid-feedback'>{t('errors.this_field_is_required')}</div>}
                  <ReactSelectComponent
                    identifier='programs'
                    options={getProgramOptions()}
                    defaultValue={getProgramOptions()}
                    formHooks={formHook}
                    isMulti={true}
                    required={false}
                    placeholder={t('labels.modal_alliance_form.programs_placeholder')}
                    formatCreateLabel={(inputValue?: string) => (`Agregar nuevo programa "${inputValue}"`)}
                    isCreatable
                    components={{
                      DropdownIndicator: null
                    }}
                  />
                </div>

              </div>
            ): null}
            {fields && fields.includes('webSite') ? (
              <div className="col-12">
                <div className="form-group">
                  <label htmlFor='webSite'>{t('labels.modal_alliance_form.webSite')}</label>
                  {formHook.errors?.webSite && <div className='invalid-feedback'>{t('errors.this_field_is_required')}</div>}
                  <input
                    id='webSite'
                    name='webSite'
                    className={`form-control ${formHook.errors?.webSite && 'is-invalid'}`}
                    type='url'
                    ref={formHook.register({ required: false })}
                    defaultValue={editData?.webSite}
                  />
                </div>
              </div>
            ): null}
            {fields && fields.includes('contact') ? (
              <div className="col-12">
                <div className="form-group">
                  <label htmlFor='contact'>{t('labels.modal_alliance_form.contact')}</label>
                  {formHook.errors?.contact && <div className='invalid-feedback'>{t('errors.this_field_is_required')}</div>}
                  <input
                    id='contact'
                    name='contact'
                    className={`form-control ${formHook.errors?.contact && 'is-invalid'}`}
                    type='email'
                    ref={formHook.register({ required: false })}
                    defaultValue={editData?.contact}
                    placeholder={t('labels.modal_alliance_form.contact_placeholder')}
                  />
                </div>
              </div>
            ): null}
            <div className="col-12">
              <div className="form-group">
                <label htmlFor='observations'>{t('labels.modal_alliance_form.observations')}</label>
                {formHook.errors?.observations && <div className='invalid-feedback'>{t('errors.this_field_is_required')}</div>}
                <textarea
                  id='observations'
                  name='observations'
                  className={`form-control ${formHook.errors?.observations && 'is-invalid'}`}
                  ref={formHook.register({ required: false })}
                  defaultValue={editData?.observations}
                />
              </div>
            </div>
            <div className='buttons-group'>
              <div className='text-center'>
                <button className='btn btn-secondary' type='submit' disabled={processing}>
                  {processing && (<><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>&nbsp;&nbsp;</>)}
                  {t('modules.modal_scheduling_form.form.save')}
                </button>
              </div>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  )
}

export default ModalAllianceFormModuleLayout
